import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { vmsService } from '../../services/vmsService';

export const UserVendorList: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  // Use the passed user object from router state, or fallback if none
  const user = location.state?.user;
  
  const [vendors, setVendors] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user) {
      // If we directly navigated without state, we could fetch user here, 
      // but for simplicity we rely on navigation from the table.
      return;
    }
    const fetchVendors = async () => {
      setLoading(true);
      try {
        // Fetch vendors belonging to this user's department
        const data = await vmsService.getVendors({ department: user.department });
        const vendorsArray = Array.isArray(data) ? data : (data?.results || []);
        setVendors(vendorsArray);
      } catch (err) {
        console.error('Failed to fetch vendors', err);
      } finally {
        setLoading(false);
      }
    };
    fetchVendors();
  }, [user]);

  if (!user) {
    return (
      <div className="page-container">
        <p>No user data found. Please navigate from the User Management page.</p>
        <button className="primary-btn" onClick={() => navigate('/admin/users')}>Go Back</button>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header" style={{ marginBottom: '1.5rem' }}>
        <div className="header-row">
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <button onClick={() => navigate(-1)} style={{ color: 'var(--text-dark)', background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', padding: 0 }} title="Back">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="24" height="24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </button>
            <h1 className="page-title" style={{ margin: 0 }}>Vendor Assignments for User</h1>
          </div>
        </div>
      </div>

      <div className="vendor-info-card" style={{ display: 'block', marginBottom: '2rem', padding: '1.5rem 2rem', background: 'white', borderRadius: '8px', border: '1px solid var(--border)' }}>
        <h3 style={{ margin: '0 0 1.25rem 0', fontSize: '1.1rem', color: 'var(--text-dark)', fontWeight: 600, borderBottom: '1px solid var(--border)', paddingBottom: '0.5rem' }}>User Details</h3>
        <div style={{ display: 'flex', gap: '3rem', flexWrap: 'wrap' }}>
          <div>
            <p className="info-label" style={{ fontSize: '0.8rem', color: 'var(--text-light)' }}>Name</p>
            <p className="info-value" style={{ fontWeight: 600 }}>{user.first_name} {user.last_name}</p>
          </div>
          <div>
            <p className="info-label" style={{ fontSize: '0.8rem', color: 'var(--text-light)' }}>Username</p>
            <p className="info-value">{user.user_name}</p>
          </div>
          <div>
            <p className="info-label" style={{ fontSize: '0.8rem', color: 'var(--text-light)' }}>Role</p>
            <p className="info-value" style={{ textTransform: 'capitalize' }}>{user.role}</p>
          </div>
          <div>
            <p className="info-label" style={{ fontSize: '0.8rem', color: 'var(--text-light)' }}>Department</p>
            <p className="info-value" style={{ color: 'var(--primary)', fontWeight: 600 }}>{user.department || 'Unassigned'}</p>
          </div>
        </div>
      </div>

      <h3 style={{ marginBottom: '1rem', color: 'var(--text-dark)' }}>Vendors in {user.department || 'this'} Department</h3>
      <div className="list-container">
        {loading ? (
          <p style={{ padding: '1rem' }}>Loading vendors...</p>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Vendor ID</th>
                <th>Vendor Name</th>
                <th>Type</th>
                <th>Contact Email</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {vendors.map(vendor => (
                <tr key={vendor.id}>
                  <td><strong>{vendor.vendor_code || vendor.id}</strong></td>
                  <td>{vendor.vendor_name}</td>
                  <td>{vendor.vendor_type || 'N/A'}</td>
                  <td>{vendor.email}</td>
                  <td>
                    <span className={`status-badge ${vendor.status === 'active' ? 'status-active' : 'status-inactive'}`}>
                      {vendor.status === 'active' ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                </tr>
              ))}
              {vendors.length === 0 && (
                <tr>
                  <td colSpan={5} style={{ textAlign: 'center', padding: '2rem' }}>No vendors found for this department.</td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};
